import type { ReactNode } from 'react';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber, pnlClass } from '@/utils/format';
import type { ClientProfitabilityRowDto } from '@/api/types/clientProfitability';

type R = ClientProfitabilityRowDto;

// LP-aware renderers (client-profitability-tables.js):
// snapshot cols -> NA when IsMissingLpSnapshot && IsLP; markUp/ea -> NA when IsLP.
const n2 = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '0.00' : formatNumber(v, 2));
const snapNa = (r: R) => r.isMissingLpSnapshot && r.isLP;
const pctOrLpNa = (r: R, v: number | null | undefined) => (snapNa(r) ? 'NA' : `${n2(v)}%`);
const numLpNa = (r: R, v: number | null | undefined) => (r.isLP ? 'NA' : n2(v));
const yesNoLpNa = (r: R, b: boolean) => (r.isLP ? 'NA' : b ? 'Yes' : 'No');

const TIP: Record<string, string> = {
  charges: 'Swap-Free Charges.',
  totalPnl: 'Total Pnl = Commission + Swap + Charges + Realized Pnl + Unrealized variation',
  commissionTurnover: 'Commission Turnover = ( Commission / Total Pnl ) * 100',
  swapTurnover: 'Swap Turnover = ( Swap / Total Pnl ) * 100',
  costTurnover: 'Cost Turnover = ( Commission + Swap + Charges / Total Pnl ) * 100',
  netPnl: 'Net Pnl = Total Pnl + MarkUp + PSP fees',
  percNetPnl: '% Net Pnl = ( Net Pnl / InOut ) * 100',
  ifSwaps: 'Swaps accumulation on Swap-Free Symbols.',
  riskyVol: 'Risky Volume = Notionals / Equity',
};
const h = (key: string, text: string): ReactNode => (TIP[key] ? <span title={TIP[key]}>{text} <i className="fe fe-info fs-11 text-muted" /></span> : text);
// PnL-colored number value (na = render NA instead when set).
const pnl = (v: number | null | undefined, na: string | null) =>
  na != null ? <span className="text-muted">{na}</span> : <span className={pnlClass(v ?? 0)}>{n2(v)}</span>;

export function ClientProfitabilityTable({
  rows,
  live,
  byUniqueId,
  loading,
  onShowTrades,
  onOpenLogin,
}: {
  rows: R[];
  live: boolean;
  byUniqueId: boolean;
  loading: boolean;
  onShowTrades: (row: R) => void;
  onOpenLogin: (login: number, platformId: number) => void;
}) {
  const idCol: DataGridColumn<R> = byUniqueId
    ? { key: 'uniqueId', title: 'Unique Id', className: 'text-start', value: (r) => r.uniqueId }
    : {
        key: 'login',
        title: 'Login',
        className: 'text-start',
        value: (r) => r.login,
        cell: (r) =>
          r.platformId ? (
            <a href="#" className="fw-semibold" onClick={(e) => { e.preventDefault(); onOpenLogin(r.login, r.platformId); }}>{r.login}</a>
          ) : (
            <span className="fw-semibold">{r.login}</span>
          ),
      };

  const columns: DataGridColumn<R>[] = [
    idCol,
    { key: 'name', title: 'Name', className: 'text-start', value: (r) => r.name },
    { key: 'group', title: 'Group', className: 'text-start', value: (r) => r.group },
    { key: 'accCurrency', title: 'Acc Currency', className: 'text-start', value: (r) => r.accCurrency },
    { key: 'country', title: 'Country', className: 'text-start', value: (r) => r.country },
    { key: 'volumeClosed', title: 'Volume Closed', className: 'text-end', value: (r) => r.volumeClosed ?? null, cell: (r) => n2(r.volumeClosed) },
    { key: 'commission', title: 'Commission', className: 'text-end', value: (r) => r.commission ?? null, cell: (r) => n2(r.commission) },
    { key: 'swap', title: 'Swap', className: 'text-end', value: (r) => r.swap ?? null, cell: (r) => n2(r.swap) },
    { key: 'charges', title: h('charges', 'Charges'), className: 'text-end', value: (r) => r.charges ?? null, cell: (r) => n2(r.charges) },
    { key: 'realizedPnl', title: 'Realized PnL', className: 'text-end', value: (r) => r.realizedPnl ?? null, cell: (r) => pnl(r.realizedPnl, null) },
    { key: 'unrealizedVariation', title: 'Unrealized Variation', className: 'text-end', value: (r) => r.unrealizedVariation ?? null, cell: (r) => (snapNa(r) ? <span className="text-muted">NA</span> : n2(r.unrealizedVariation)) },
    { key: 'totalPnl', title: h('totalPnl', 'Total PnL'), className: 'text-end', value: (r) => r.totalPnl ?? null, cell: (r) => pnl(r.totalPnl, snapNa(r) ? 'NA' : null) },
    { key: 'commissionTurnover', title: h('commissionTurnover', 'Commission Turnover'), className: 'text-end', value: (r) => r.commissionTurnover ?? null, cell: (r) => pctOrLpNa(r, r.commissionTurnover) },
    { key: 'swapTurnover', title: h('swapTurnover', 'Swap Turnover'), className: 'text-end', value: (r) => r.swapTurnover ?? null, cell: (r) => pctOrLpNa(r, r.swapTurnover) },
    { key: 'costTurnover', title: h('costTurnover', 'Cost Turnover'), className: 'text-end', value: (r) => r.costTurnover ?? null, cell: (r) => pctOrLpNa(r, r.costTurnover) },
    { key: 'netInOut', title: 'Net In Out', className: 'text-end', value: (r) => r.netInOut ?? null, cell: (r) => n2(r.netInOut) },
    { key: 'ea', title: 'EA', className: 'text-center', value: (r) => (r.isLP ? 'NA' : r.ea ? 'Yes' : 'No'), cell: (r) => yesNoLpNa(r, r.ea) },
    { key: 'markUp', title: 'MarkUp', className: 'text-end', value: (r) => r.markUp ?? null, cell: (r) => numLpNa(r, r.markUp) },
    { key: 'pspFees', title: 'PSP fees', className: 'text-end', value: (r) => r.pspFees ?? null, cell: (r) => n2(r.pspFees) },
    { key: 'netPnl', title: h('netPnl', 'Net Pnl'), className: 'text-end', value: (r) => r.netPnl ?? null, cell: (r) => pnl(r.netPnl, snapNa(r) ? 'NA' : null) },
    { key: 'percNetPnl', title: h('percNetPnl', '% Net Pnl'), className: 'text-end', value: (r) => r.percNetPnl ?? null, cell: (r) => pctOrLpNa(r, r.percNetPnl) },
    { key: 'ifSwaps', title: h('ifSwaps', 'IfSwaps'), className: 'text-end', value: (r) => r.ifSwaps ?? null, cell: (r) => n2(r.ifSwaps) },
    { key: 'connector', title: 'Connector', className: 'text-start', value: (r) => r.connector },
  ];

  if (live) {
    columns.push(
      { key: 'unrealizedCommission', title: 'Unrealized Commission', className: 'text-end', value: (r) => r.unrealizedCommission ?? null, cell: (r) => n2(r.unrealizedCommission) },
      { key: 'unrealizedSwap', title: 'Unrealized Swap', className: 'text-end', value: (r) => r.unrealizedSwap ?? null, cell: (r) => n2(r.unrealizedSwap) },
      { key: 'unrealizedPnl', title: 'Unrealized PnL', className: 'text-end', value: (r) => r.unrealizedPnl ?? null, cell: (r) => pnl(r.unrealizedPnl, null) },
      { key: 'totalUnrealizedPnl', title: 'Total Unrealized Pnl', className: 'text-end', value: (r) => r.totalUnrealizedPnl ?? null, cell: (r) => pnl(r.totalUnrealizedPnl, null) },
      { key: 'riskyVol', title: h('riskyVol', 'RiskyVol'), className: 'text-end', value: (r) => r.riskyVol ?? null, cell: (r) => n2(r.riskyVol) },
    );
  }

  return (
    <DataGrid
      columns={columns}
      data={loading ? [] : rows}
      rowKey={(r) => `${r.platformId}:${byUniqueId ? r.uniqueId : r.login}`}
      card
      includeExcel
      excelFileName="client-profitability"
      emptyMessage={loading ? 'Loading…' : 'No data for the current filters.'}
      rowActions={(r) => (
        <button type="button" className="btn btn-xs btn-outline-primary" title="Show Trades" onClick={() => onShowTrades(r)}>
          <i className="fa fa-file-text-o me-1" />
          Show Trades
        </button>
      )}
    />
  );
}
